import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusPill } from "@/components/shared/StatusPill";
import { BusinessStage } from "@/components/shared/BusinessStage";
import { AIBadge } from "@/components/shared/AICard";
import { Loading } from "@/components/shared/Loading";
import { EmptyState } from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { useApprovals, useMe, useAction, useRole } from "@/api/hooks";
import { toast } from "sonner";
import { Check, X, CheckSquare, ExternalLink } from "lucide-react";

export default function Approvals() {
  const { data: approvals } = useApprovals();
  const { data: me } = useMe();
  const act = useAction(["approvals"]);
  const navigate = useNavigate();
  const role = useRole();
  if (!approvals || !me) return <Loading />;

  const canApprove = me.caps.approve;
  const pending = approvals.filter((a) => a.status === "Pending");

  const decide = (aid, decision) => act.mutate({ url: `/approvals/${aid}/decision`, body: { decision } }, { onSuccess: () => toast.success(`${decision}`), onError: () => toast.error("Not permitted") });

  return (
    <>
      <PageHeader title="Approvals" subtitle="Contextual workflow approvals — credit, margin, onboarding, variance" />
      <div className="flex-1 overflow-y-auto p-6 space-y-3">
        {pending.length === 0 && <EmptyState icon={CheckSquare} title="No approvals pending" sub="You're all caught up." />}
        {approvals.map((a) => {
          const decided = a.status !== "Pending";
          return (
            <div key={a.id} data-testid={`approval-${a.id}`} className={`rounded-xl border bg-white p-4 ${decided ? "border-slate-200 opacity-70" : "border-amber-200"}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><span className="font-display font-semibold text-slate-800">{a.type}</span><StatusPill status={a.status === "Pending" ? "Approval Pending" : a.status} /></div>
                  <div className="text-[13px] text-slate-600 mt-1">{a.context}</div>
                  <div className="text-[11px] text-slate-400 mt-1">Requested by {a.requester} · Rule: {a.threshold} · {a.impact}</div>
                  {a.businessStage && (
                    <div className="mt-2 flex min-w-0 items-center gap-2">
                      <BusinessStage stage={a.businessStage} compact />
                      <span className="truncate text-[11px] text-slate-500">{a.businessStage.name} · Last status: {a.businessStage.lastStatus}</span>
                    </div>
                  )}
                </div>
                {a.customerId && (
                  <Button size="sm" variant="outline" className="h-8 shrink-0" data-testid={`approval-openlead-${a.id}`}
                    onClick={() => navigate(`/${role}/customers/${a.customerId}`)}>
                    <ExternalLink className="h-3.5 w-3.5 mr-1.5" />Open lead
                  </Button>
                )}
              </div>
              <div className="mt-3 rounded-lg border border-violet-200 bg-violet-50/70 p-3 flex items-start gap-2"><AIBadge>Recommendation</AIBadge><span className="text-[13px] text-violet-950 flex-1">{a.ai}</span></div>
              {!decided && (
                <div className="flex items-center justify-end gap-2 mt-3">
                  {canApprove ? (
                    <>
                      <Button size="sm" variant="outline" onClick={() => decide(a.id, "Rejected")} data-testid={`approval-reject-${a.id}`}><X className="h-3.5 w-3.5 mr-1" />Reject</Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => decide(a.id, "Approved")} data-testid={`approval-approve-${a.id}`}><Check className="h-3.5 w-3.5 mr-1" />Approve</Button>
                    </>
                  ) : <span className="text-[12px] text-slate-400">Your role can view but not decide this approval.</span>}
                </div>
              )}
              {decided && a.decidedBy && <div className="mt-2 text-[11px] text-slate-400">Decided by {a.decidedBy}</div>}
            </div>
          );
        })}
      </div>
    </>
  );
}
